import type {
  CreateWebhookInput,
  Page,
  UpdateWebhook,
  WebhookSubscription,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/webhooks');

/** `GET /admin/v1/webhooks` */
export function useWebhooks(pageSize = DEFAULT_PAGE_SIZE) {
  const { request, keys } = useApiContext();
  return usePagedList<WebhookSubscription>({
    queryKey: keys.webhooks.list({ pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<WebhookSubscription>>('GET', base, {
        query: pageQuery(cursor, pageSize),
        signal,
      }),
  });
}

export function useWebhook(webhookId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.webhooks.detail(webhookId ?? ''),
    queryFn: ({ signal }) =>
      request<WebhookSubscription>('GET', `${base}/${webhookId}`, { signal }),
    enabled: webhookId !== undefined,
  });
}

export function useCreateWebhook() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateWebhookInput) =>
      request<WebhookSubscription>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.webhooks.all),
  });
}

export function useUpdateWebhook() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateWebhook }) =>
      request<WebhookSubscription>('PATCH', `${base}/${id}`, { body: patch }),
    onSuccess: (_w, { id }) => invalidate(keys.webhooks.all, keys.webhooks.detail(id)),
  });
}

export function useDeleteWebhook() {
  const { request, keys, invalidate, remove } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<void>('DELETE', `${base}/${id}`),
    onSuccess: (_void, id) => {
      remove(keys.webhooks.detail(id));
      return invalidate(keys.webhooks.all);
    },
  });
}
